/**
 * Role-based charge filtering service for Closing Disclosure
 *
 * Provides utilities to filter charges by role (buyer, seller, lender, title company, agent)
 * and calculate role-specific totals. Works with the accounting/involved_parties data structure.
 *
 * Data Flow:
 *   Order → accounting[] → AccountingType → involved_parties[] → AccountingInvolvedParty
 *   Each involved_parties member has: role/kind, at_closing_amount, before_closing_amount
 *
 * Direction terminology:
 *   - 'payer': role owes/pays this charge (consumes money)
 *   - 'payee': role receives/is owed this charge (receives money)
 */

import { NewOrderType } from 'types/order';
import { PartyRole } from 'types/closing-disclosure';
import { AccountingType, AccountingInvolvedParty } from 'v2-types/order';

/**
 * Determine the role/kind of a party
 *
 * Maps order_kinds array (like ["buyer", "agent"]) to PartyRole enum values.
 * A party can have multiple kinds; this returns all matching roles.
 *
 * @param party - The involved party object
 * @returns Array of PartyRole enum values that match this party's kinds
 */
function getPartyRoles(party: AccountingInvolvedParty): PartyRole[] {
  if (!party || !party.order_kinds || !Array.isArray(party.order_kinds)) {
    return [];
  }

  const roleMap: Record<string, PartyRole> = {
    buyer: PartyRole.BUYER,
    seller: PartyRole.SELLER,
    lender: PartyRole.LENDER,
    title_company: PartyRole.TITLE_COMPANY,
    agent: PartyRole.AGENT,
  };

  const roleSet = new Set<PartyRole>();
  party.order_kinds.forEach((kind) => {
    const role = roleMap[kind];
    if (role) {
      roleSet.add(role);
    }
  });

  return Array.from(roleSet);
}

/**
 * Check if a role appears in a charge's involved_parties
 *
 * @param charge - Accounting entry
 * @param roleId - PartyRole enum value to search for
 * @returns true if this role has any activity (payer or payee) in this charge
 */
function roleHasActivityInCharge(charge: AccountingType, roleId: PartyRole): boolean {
  if (!charge || !charge.involved_parties || !Array.isArray(charge.involved_parties)) {
    return false;
  }

  return charge.involved_parties.some((party) => {
    const roles = getPartyRoles(party);
    return roles.includes(roleId);
  });
}

/**
 * Get all roles that have activity in this order
 *
 * Scans all accounting entries and identifies unique roles that appear
 * in the involved_parties across all charges.
 *
 * @param order - The order object (or null/undefined)
 * @returns Array of PartyRole enum values for roles with activity
 *
 * @example
 *   const roles = getRolesInvolved(order);
 *   // returns [PartyRole.BUYER, PartyRole.SELLER, PartyRole.LENDER, PartyRole.TITLE_COMPANY]
 */
export function getRolesInvolved(
  order: NewOrderType | null | undefined,
): PartyRole[] {
  if (!order || !order.accounting || !Array.isArray(order.accounting)) {
    return [];
  }

  const rolesSet = new Set<PartyRole>();

  order.accounting.forEach((charge) => {
    if (charge.involved_parties && Array.isArray(charge.involved_parties)) {
      charge.involved_parties.forEach((party) => {
        const roles = getPartyRoles(party);
        roles.forEach((role) => {
          rolesSet.add(role);
        });
      });
    }
  });

  return Array.from(rolesSet);
}

/**
 * Determine if a role is payer, payee, or both for a specific charge
 *
 * "Payer" = the role pays/owes this charge (has at_closing_amount + before_closing_amount on negative side)
 * "Payee" = the role receives this charge (has positive amounts as recipient)
 *
 * In the involved_parties structure, amounts are signed:
 * - Negative amounts indicate the role owes/pays
 * - Positive amounts indicate the role receives/is credited
 *
 * @param charge - The accounting entry
 * @param roleId - PartyRole to check
 * @returns Object with is_payer and is_payee flags
 */
export function getRoleParticipation(
  charge: AccountingType | null | undefined,
  roleId: PartyRole,
): { is_payer: boolean; is_payee: boolean } {
  const result = { is_payer: false, is_payee: false };

  if (!charge || !charge.involved_parties || !Array.isArray(charge.involved_parties)) {
    return result;
  }

  charge.involved_parties.forEach((party) => {
    const roles = getPartyRoles(party);
    if (!roles.includes(roleId)) {
      return;
    }

    const totalAmount = (party.at_closing_amount || 0) + (party.before_closing_amount || 0);

    if (totalAmount > 0) {
      // eslint-disable-next-line no-param-reassign
      result.is_payee = true;
    } else if (totalAmount < 0) {
      // eslint-disable-next-line no-param-reassign
      result.is_payer = true;
    }
  });

  return result;
}

/**
 * Get charges where specified role is payer or payee
 *
 * Filters the order's accounting charges to return only those where the role
 * has relevant activity in the specified direction.
 *
 * Direction:
 *   - 'payer': role owes/pays this charge (charges the role is responsible for)
 *   - 'payee': role receives this charge (credits/disbursements to the role)
 *
 * @param order - The order object (or null/undefined)
 * @param roleId - PartyRole enum value to filter for
 * @param direction - 'payer' or 'payee'
 * @returns Array of accounting charges for this role in the specified direction
 *
 * @example
 *   const buyerCharges = getChargesForRole(order, PartyRole.BUYER, 'payer');
 *   // returns all charges the buyer is responsible for paying
 *
 *   const lenderDisbursements = getChargesForRole(order, PartyRole.LENDER, 'payee');
 *   // returns all charges where lender is the payee (loan funding, etc.)
 */
export function getChargesForRole(
  order: NewOrderType | null | undefined,
  roleId: PartyRole,
  direction: 'payer' | 'payee',
): AccountingType[] {
  if (!order || !order.accounting || !Array.isArray(order.accounting)) {
    return [];
  }

  return order.accounting.filter((charge) => {
    const participation = getRoleParticipation(charge, roleId);

    if (direction === 'payer') {
      return participation.is_payer;
    }

    if (direction === 'payee') {
      return participation.is_payee;
    }

    return false;
  });
}

/**
 * Calculate total owed/received by role
 *
 * Sums all at_closing and before_closing amounts for this role across
 * all charges in the specified direction.
 *
 * Direction:
 *   - 'payer': returns total amount role owes (sum of negative amounts)
 *   - 'payee': returns total amount role receives (sum of positive amounts)
 *
 * Result is returned in cents (as integers, never decimals).
 *
 * @param order - The order object (or null/undefined)
 * @param roleId - PartyRole enum value
 * @param direction - 'payer' or 'payee'
 * @returns Total in cents (as integer; can be negative for payer direction)
 *
 * @example
 *   const buyerTotal = calculateRoleTotal(order, PartyRole.BUYER, 'payer');
 *   // returns 12902750 (meaning $129,027.50 the buyer owes)
 *
 *   const sellerProceeds = calculateRoleTotal(order, PartyRole.SELLER, 'payee');
 *   // returns 33319300 (meaning $333,193.00 seller receives)
 */
export function calculateRoleTotal(
  order: NewOrderType | null | undefined,
  roleId: PartyRole,
  direction: 'payer' | 'payee',
): number {
  if (!order || !order.accounting || !Array.isArray(order.accounting)) {
    return 0;
  }

  let total = 0;

  order.accounting.forEach((charge) => {
    if (charge.involved_parties && Array.isArray(charge.involved_parties)) {
      charge.involved_parties.forEach((party) => {
        const roles = getPartyRoles(party);
        if (!roles.includes(roleId)) {
          return;
        }

        const atClosing = party.at_closing_amount || 0;
        const beforeClosing = party.before_closing_amount || 0;
        const partyTotal = atClosing + beforeClosing;

        if (direction === 'payer' && partyTotal < 0) {
          // eslint-disable-next-line no-param-reassign
          total += partyTotal;
        } else if (direction === 'payee' && partyTotal > 0) {
          // eslint-disable-next-line no-param-reassign
          total += partyTotal;
        }
      });
    }
  });

  return total;
}

/**
 * Filter charges by multiple roles at once
 *
 * Returns charges where ANY of the specified roles participates in the given direction.
 * Useful for grouping charges for multiple parties (e.g., all sellers).
 *
 * @param order - The order object (or null/undefined)
 * @param roleIds - Array of PartyRole enum values to include
 * @param direction - 'payer' or 'payee'
 * @returns Array of accounting charges involving any of the specified roles
 *
 * @example
 *   const sellerCharges = getChargesForRoles(order, [PartyRole.SELLER], 'payer');
 *   // returns all charges sellers are responsible for
 */
export function getChargesForRoles(
  order: NewOrderType | null | undefined,
  roleIds: PartyRole[],
  direction: 'payer' | 'payee',
): AccountingType[] {
  if (!roleIds || roleIds.length === 0) {
    return [];
  }

  const allCharges: AccountingType[] = [];
  const seenChargeIds = new Set<string>();

  roleIds.forEach((roleId) => {
    const roleCharges = getChargesForRole(order, roleId, direction);
    roleCharges.forEach((charge) => {
      if (!seenChargeIds.has(charge._id)) {
        seenChargeIds.add(charge._id);
        allCharges.push(charge);
      }
    });
  });

  return allCharges;
}
